import {
  Button,
  Divider,
  Group,
  PasswordInput,
  SimpleGrid,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { showNotification } from '@mantine/notifications';
import axios from 'axios';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Check, ExclamationMark } from 'tabler-icons-react';
import { z } from 'zod';

import { useAuth } from 'api/auth';

import { RegisterFormValues } from '../types';
import AuthLayout from './AuthLayout';

export function RegisterForm() {
  const { t } = useTranslation();
  const { register, isRegistering } = useAuth();
  const navigate = useNavigate();

  const registerSchema = useMemo(
    () =>
      z.object({
        firstName: z
          .string()
          .trim()
          .min(1, { message: t('auth.validation.firstNameRequired') }),
        lastName: z
          .string()
          .trim()
          .min(1, { message: t('auth.validation.lastNameRequired') }),
        email: z
          .string()
          .email({ message: t('auth.validation.emailInvalid') })
          .min(1, { message: t('auth.validation.emailRequired') }),
        password: z
          .string()
          .min(6, { message: t('auth.validation.passwordMin') }),
      }),
    [t]
  );

  const form = useForm<RegisterFormValues>({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
    },
    validate: zodResolver(registerSchema),
    validateInputOnChange: ['password'],
  });

  const handleSubmit = (values: RegisterFormValues) => {
    register(values, {
      onSuccess: (data: {
        message?: string;
        token?: string;
      }) => {
        if (data?.message && !data?.token) {
          showNotification({
            title: t('auth.register.registrationReceived'),
            message: data.message,
            color: 'green',
            icon: <Check />,
          });
          navigate('/login', { replace: true });
          return;
        }
        navigate('/dashboard', { replace: true });
      },
      onError: (err) => {
        if (axios.isAxiosError(err)) {
          if (err.response?.status === 409) {
            form.setFieldError(
              'email',
              t('auth.register.emailTaken'),
            );
            return;
          }
        }
        showNotification({
          title: t('common.serverError'),
          message: t('common.tryAgainLater'),
          color: 'red',
          icon: <ExclamationMark />,
        });
      },
    });
  };

  return (
    <AuthLayout
      title="Create Account"
      subtitle={t('auth.register.subtitle')}
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <SimpleGrid
          cols={2}
          breakpoints={[{ maxWidth: 'xs', cols: 1 }]}
          mb="md"
        >
          <TextInput
            label={t('auth.register.firstName')}
            placeholder="Jane"
            radius="md"
            size="md"
            {...form.getInputProps('firstName')}
          />
          <TextInput
            label={t('auth.register.lastName')}
            placeholder="Doe"
            radius="md"
            size="md"
            {...form.getInputProps('lastName')}
          />
        </SimpleGrid>

        <TextInput
          label={t('auth.email')}
          placeholder={t('auth.login.emailPlaceholder')}
          mb="md"
          radius="md"
          size="md"
          {...form.getInputProps('email')}
        />

        <PasswordInput
          label={t('auth.password')}
          placeholder={t('auth.register.passwordPlaceholder')}
          mb={4}
          radius="md"
          size="md"
          description={
            form.errors.password || form.isValid('password')
              ? null
              : t('auth.register.passwordHint')
          }
          inputWrapperOrder={[
            'label',
            'input',
            'description',
            'error',
          ]}
          styles={{ error: { marginTop: 5 } }}
          {...form.getInputProps('password')}
        />
        {form.isValid('password') && (
          <Group spacing={4} mt={4} mb="md">
            <Check size={14} color="#12b886" />
            <Text size="xs" color="teal.7">
              {t('auth.register.passwordOk')}
            </Text>
          </Group>
        )}

        <Button
          type="submit"
          loading={isRegistering}
          fullWidth
          mt="lg"
          size="md"
          radius="md"
          variant="gradient"
          gradient={{ from: 'indigo', to: 'violet' }}
          sx={{
            boxShadow:
              '0 4px 14px rgba(99,102,241,0.3)',
          }}
        >
          {t('auth.signUp')}
        </Button>
      </form>

      <Divider
        my="lg"
        styles={{
          label: { fontSize: 12, color: '#9ca3af' },
        }}
      />

      <Text size="sm" c="dimmed" ta="center">
        {t('auth.register.alreadyHaveAccount')}
        {' '}
        <Text
          component={Link}
          to="/login"
          size="sm"
          weight={600}
          color="violet.6"
          inherit
        >
          {t('common.signIn')}
        </Text>
      </Text>
    </AuthLayout>
  );
}
